import React, { useCallback, useState } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useSnackbar } from 'notistack';

import { SectionHeading } from '../../_core/_ui/typography.component';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { DashboardCard } from '../../_core/_ui/dashboard-card.component';
import { useParentUpdateName } from '../../../lib/services/parent.service';
import { IParentSelf } from '../../../lib/types';

interface IParentNameCardProps {
  parent: IParentSelf;
}

// Edits the parent's display name. The server cascades to every
// PlayerContact owned by this parent.
export const ParentNameCard: React.FC<IParentNameCardProps> = ({ parent }) => {
  const [draft, setDraft] = useState(parent.name);
  const [updateName, { loading }] = useParentUpdateName();
  const { enqueueSnackbar } = useSnackbar();

  // Trim before comparing so leading/trailing spaces don't masquerade as a
  // pending change. The mutation submits the trimmed value too.
  const trimmed = draft.trim();
  const dirty = trimmed.length > 0 && trimmed !== parent.name;

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dirty || loading) return;
    try {
      await updateName({ variables: { value: trimmed } });
      enqueueSnackbar('Your name has been updated everywhere.', { variant: 'success' });
    } catch (err) {
      console.error('parentUpdateName failed:', err);
      enqueueSnackbar('Could not update your name. Please try again.', { variant: 'error' });
    }
  }, [dirty, loading, trimmed, updateName, enqueueSnackbar]);

  return (
    <DashboardCard>
      <form onSubmit={handleSubmit}>
        <SectionHeading>Your name</SectionHeading>
        {/* MUI v4 Box has no `gap` prop — use inline style for the flex gap. */}
        <Box mt={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 16 }}>
          <TextField
            label="Full name"
            variant="outlined"
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{ 'aria-label': 'Your name', maxLength: 120 }}
            style={{ flex: '1 1 240px' }}
          />
          <PrimaryButton
            type="submit"
            variant="contained"
            disabled={!dirty || loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </PrimaryButton>
        </Box>
      </form>
    </DashboardCard>
  );
};
